import React, { useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthForm from "./components/AuthForm";
import Onboarding from "./components/Onboarding";
import BackButtonHandler from "./components/BackButtonHandler";
import NotificationClickHandler from "./components/NotificationClickHandler";
import { initOneSignal } from "./services/pushService";

// Inizializza OneSignal una sola volta all'avvio (no-op su web)
initOneSignal();

// Lazy load pages
const HomePage = React.lazy(() => import("./pages/HomePage"));
const VehiclesPage = React.lazy(() => import("./pages/VehiclesPage"));
const VehicleDetailPage = React.lazy(() => import("./pages/VehicleDetailPage"));
const HistoryPage = React.lazy(() => import("./pages/HistoryPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const VisitorPage = React.lazy(() => import("./pages/VisitorPage"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const DeleteAccountPage = React.lazy(() => import("./pages/DeleteAccountPage"));

const ProtectedRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  const [onboardingDone, setOnboardingDone] = useState(
    () => !!localStorage.getItem('sf_onboarding_done')
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 animate-spin border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <p className="text-gray-600">Caricamento…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthForm />;
  }

  if (!onboardingDone) {
    return <Onboarding onDone={() => setOnboardingDone(true)} />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/vehicles" element={<VehiclesPage />} />
      <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
      <Route path="/history" element={<HistoryPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="*" element={<HomePage />} />
    </Routes>
  );
};

const PageSuspense = ({ children }) => (
  <React.Suspense
    fallback={
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 animate-spin border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <p className="text-gray-600">Caricamento pagina...</p>
        </div>
      </div>
    }
  >
    {children}
  </React.Suspense>
);

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          {/* Dentro il Router: gli serve sapere su quale schermata siamo */}
          <BackButtonHandler />
          {/* Dentro il Router: deve poter portare alla Home quando tocchi la notifica */}
          <NotificationClickHandler />
          <PageSuspense>
            <Routes>
              {/* Rotta pubblica: visitatori che scansionano il QR (no auth) */}
              <Route path="/visitor/:userId" element={<VisitorPage />} />
              {/* Rotta pubblica: privacy policy (no auth, linkabile da store) */}
              <Route path="/privacy" element={<PrivacyPage />} />
              {/* Rotte pubbliche: recupero password */}
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              {/* Tutto il resto richiede autenticazione */}
              <Route path="/*" element={<ProtectedRoutes />} />
            </Routes>
          </PageSuspense>
          <Toaster />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
